<script setup lang="ts">
  import Popover from '@/components/ui/popover/Popover.vue'
  import PopoverContent from '@/components/ui/popover/PopoverContent.vue'
  import PopoverTrigger from '@/components/ui/popover/PopoverTrigger.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { genericErrorToast, genericSuccessToast } from '@/lib/toasts'
  import { DocumentsService } from '@/types/generated'
  import { FileTextIcon, X } from 'lucide-vue-next'
  import { computed, ref } from 'vue'
  import DocumentsList from './DocumentsList.vue'
  import DocumentsUpload from './DocumentsUpload.vue'

  const { t } = useTranslation({
    en: {
      Documents: 'Documents',
      UploadDocuments: 'Upload Documents'
    },
    de: {
      Documents: 'Dokumente',
      UploadDocuments: 'Dokumente hochladen'
    }
  })

  const props = withDefaults(
    defineProps<{
      documentProcessor: any
      title?: string
      showUpload?: boolean
      showDelete?: boolean
      showDownload?: boolean
      showEdit?: boolean
      showRetry?: boolean
      compact?: boolean
      onDocumentDeleted?: (documentId: string) => Promise<void>
      onDocumentRetry?: (documentId: string) => Promise<void>
      onUploadComplete?: () => Promise<void>
    }>(),
    {
      title: 'Documents',
      showUpload: true,
      showDelete: true,
      showDownload: true,
      showEdit: false,
      showRetry: true,
      compact: false
    }
  )

  // Use the provided processor directly
  const documentProcessor = computed(() => props.documentProcessor)

  const emit = defineEmits<{
    documentDeleted: [documentId: string]
    documentRetry: [documentId: string]
    fileUploaded: [documentId: string]
    filesSelected: [files: File[]]
    uploadComplete: []
    refreshNeeded: []
  }>()

  const isOpen = ref(false)

  // Count from processor documents
  const documentCount = computed(() => {
    return documentProcessor.value?.documents?.length || 0
  })

  // Handle events from DocumentsList
  const handleDocumentDeleted = async (documentId: string) => {
    emit('documentDeleted', documentId)
    if (props.onDocumentDeleted) {
      await props.onDocumentDeleted(documentId)
    }
  }

  const handleDocumentRetry = async (documentId: string) => {
    try {
      await DocumentsService.documentsRetryProcessingCreate({
        uuid: documentId
      })
      genericSuccessToast()
      emit('documentRetry', documentId)
      if (props.onDocumentRetry) {
        await props.onDocumentRetry(documentId)
      }
    } catch (error) {
      console.error('Error retrying document:', error)
      genericErrorToast()
    }
  }

  // Handle events from DocumentsUpload
  const handleFileUploaded = (documentId: string) => {
    emit('fileUploaded', documentId)
  }

  const handleFilesSelected = (files: File[]) => {
    emit('filesSelected', files)
  }

  const handleUploadComplete = async () => {
    emit('uploadComplete')
    if (props.onUploadComplete) {
      await props.onUploadComplete()
    }
  }
</script>

<template>
  <Popover
    :open="isOpen"
    @update:open="isOpen = $event"
  >
    <PopoverTrigger as-child>
      <slot name="trigger">
        <button
          type="button"
          class="flex items-center justify-center gap-x-1.5 rounded-md border border-gray-300 px-2.5 py-1 text-sm
            font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500
            focus:ring-offset-2"
        >
          <FileTextIcon class="size-4 shrink-0" />
          {{ t('UploadDocuments') }}
        </button>
      </slot>
    </PopoverTrigger>

    <PopoverContent
      class="w-96 p-0"
      side="bottom"
      align="end"
      :side-offset="8"
    >
      <!-- Header -->
      <div class="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <h3 class="text-lg font-medium">{{ title }} ({{ documentCount }})</h3>
        <button
          class="text-gray-500 hover:text-gray-700"
          @click="isOpen = false"
        >
          <X class="h-5 w-5" />
        </button>
      </div>

      <div class="flex max-h-[500px] flex-col">
        <!-- Documents List (Scrollable) -->
        <div class="flex-1 overflow-y-auto p-4 px-2">
          <DocumentsList
            :show-delete="showDelete"
            :show-download="showDownload"
            :show-edit="showEdit"
            :show-retry="showRetry"
            :compact="compact"
            :document-processor="documentProcessor"
            @document-deleted="handleDocumentDeleted"
            @document-retry="handleDocumentRetry"
          />
        </div>

        <!-- Upload Area (Fixed at bottom) -->
        <div
          v-if="showUpload"
          class="mt-auto border-t border-gray-200 p-4"
        >
          <DocumentsUpload
            :compact="compact"
            :document-processor="documentProcessor"
            @file-uploaded="handleFileUploaded"
            @files-selected="handleFilesSelected"
            @upload-complete="handleUploadComplete"
          />
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>
